import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import profile from 'src/store/profile';
import api from 'src/utils/api';
// @mui
import {
  Alert,
  Container,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Stepper,
  Step,
  Box,
  Button,
  StepLabel,
  StepButton,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  Snackbar,
  IconButton,
} from '@mui/material';

// components
import { CreateMessage } from 'src/sections/@dashboard/student';

// ----------------------------------------------------------------------

const MIN_TASK_TIME_MS = 60000;

const TaskIframe = ({ htmlContent, onInteract }) => {
  const [loading, setLoading] = useState(true);
  const [isLocked, setIsLocked] = useState(true);

  useEffect(() => {
    setLoading(true);
    setIsLocked(true);
  }, [htmlContent]);

  if (!htmlContent) return null;

  const srcMatch = htmlContent.match(/src="([^"]+)"/);
  const src = srcMatch ? srcMatch[1] : null;

  if (!src) {
    // eslint-disable-next-line react/no-danger
    return <div className="iframe" dangerouslySetInnerHTML={{ __html: htmlContent }} />;
  }

  const handleStartClick = () => {
    setIsLocked(false);
    if (onInteract) {
      onInteract();
    }
  };

  return (
    <Box 
      sx={{ 
        position: 'relative', 
        width: '100%', 
        height: '360px', 
        bgcolor: '#fff', 
        borderRadius: 3, 
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
        overflow: 'hidden',
        maskImage: 'radial-gradient(white, black)',
        WebkitMaskImage: '-webkit-radial-gradient(white, black)',
        transform: 'translate3d(0,0,0)', 
      }}
    >
      <style>
        {`
          @keyframes crawl {
            to { stroke-dashoffset: -100; }
          }
          .worm-rect {
            animation: crawl 20s linear infinite;
          }
        `}
      </style>
      
      {loading && (
        <Box
          sx={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1,
            bgcolor: 'white'
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {!loading && isLocked && (
        <Box
          onClick={handleStartClick}
          sx={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
            zIndex: 10,
            bgcolor: '#FFF59D',
            cursor: 'pointer',
            transition: 'all 0.4s ease',
            '&:hover': {
              bgcolor: '#FFF176',
              '& .play-button': { transform: 'scale(1.1)' }
            },
            p: 3, 
          }}
        >
          <Box sx={{ position: 'absolute', top: 24, left: 24, right: 24, bottom: 24, pointerEvents: 'none' }}>
            <svg width="100%" height="100%" style={{ overflow: 'visible' }}>
              <rect
                className="worm-rect"
                x="6" y="6" 
                width="calc(100% - 12px)" 
                height="calc(100% - 12px)"
                rx="16" ry="16"
                fill="none"
                stroke="#FF6D00" 
                strokeWidth="12" 
                strokeLinecap="round"
                pathLength="100"
                strokeDasharray="12.5 87.5" 
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          </Box>

          <Box 
            className="play-button"
            sx={{ 
              width: 90, 
              height: 90, 
              bgcolor: '#FF6D00', 
              borderRadius: '50%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              boxShadow: '0 10px 30px rgba(255, 109, 0, 0.3)',
              transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              position: 'relative', 
              zIndex: 2 
            }}
          >
             <svg width="34" height="34" viewBox="0 0 24 24" fill="white" 
               style={{ display: 'block', transform: 'translateX(3px)' }}
             >
                <path d="M18.2 10.6L7.8 4.2C6.5 3.4 4.9 4.4 4.9 5.9V18.1C4.9 19.6 6.5 20.6 7.8 19.8L18.2 13.4C19.5 12.6 19.5 10.6 18.2 10.6Z" />
             </svg>
          </Box>
        </Box>
      )}

      <iframe
        src={src}
        onLoad={() => setLoading(false)}
        title="Interactive Task"
        style={{
          width: '100%', height: '100%', border: 'none',
          display: 'block',
          opacity: loading ? 0 : 1, transition: 'opacity 0.5s',
        }}
        allowFullScreen
      />
    </Box>
  );
};

TaskIframe.propTypes = {
  htmlContent: PropTypes.string,
  onInteract: PropTypes.func,
};

export default function Homeworks() {
  const studentId = profile.data?.id || 'default';

  const [tab, setTab] = useState(() => {
    const savedTab = localStorage.getItem(`lala_tab_${studentId}`);
    return savedTab ? parseInt(savedTab, 10) : 1;
  });

  const [check, setCheck] = useState(0);
  const [lessons, setLessons] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [maxStep, setMaxStep] = useState(0);
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  
  const [isTaskInteracted, setIsTaskInteracted] = useState(false);
  const [interactionStartTime, setInteractionStartTime] = useState(null);

  const [vocabLines, setVocabLines] = useState([]);
  const [vocabIndex, setVocabIndex] = useState(0);
  const [vocabRevealed, setVocabRevealed] = useState(false);

  const handleTabChange = (_, value) => {
    if (value !== null) {
      setTab(value);
      localStorage.setItem(`lala_tab_${studentId}`, value);
    }
  };

  const handleLessonChange = (event) => {
    const newCheck = event.target.value;
    setCheck(newCheck);
    if (lessons[newCheck]) {
      localStorage.setItem(`lala_lesson_${studentId}`, lessons[newCheck].id);
    }
  };

  useEffect(() => {
    if (lessons.length > 0 && lessons[check]) {
      const lessonId = lessons[check].id;
      const savedProgress = localStorage.getItem(`lala_progress_${studentId}_${lessonId}`);
      const parsedStep = savedProgress ? parseInt(savedProgress, 10) : 0;
      
      setMaxStep(parsedStep);
      setActiveStep(parsedStep > 4 ? 4 : parsedStep);
    }
  }, [lessons, check, studentId]);

  useEffect(() => {
    if (lessons.length > 0 && lessons[check]) {
      const lessonId = lessons[check].id;
      localStorage.setItem(`lala_progress_${studentId}_${lessonId}`, maxStep);
    }
  }, [maxStep, lessons, check, studentId]);

  const capitalizeWords = (str) => {
    if (!str) return '';
    return String(str).trim().split(/\s+/).map((w) => (w.length ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(' ');
  };

  const handleSpeak = (text, e) => {
    e.stopPropagation(); 
    if (!text) return;

    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    
    let voices = window.speechSynthesis.getVoices();
    
    if (voices.length === 0) {
      window.speechSynthesis.onvoiceschanged = () => {
        voices = window.speechSynthesis.getVoices();
      };
    }

    const preferredVoice = voices.find(v => v.name.includes('Google US English')) 
                        || voices.find(v => v.name.includes('Samantha')) 
                        || voices.find(v => v.lang === 'en-US')
                        || voices.find(v => v.lang.startsWith('en'));

    if (preferredVoice) {
      utterance.voice = preferredVoice;
      utterance.lang = preferredVoice.lang; 
    } else {
      utterance.lang = 'en-US';
    }

    utterance.pitch = 1.0; 
    utterance.rate = 0.9;  
    utterance.volume = 1.0; 

    window.speechSynthesis.speak(utterance);
  };

  const handlePrevWord = () => {
    setVocabRevealed(false);
    setVocabIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNextWord = () => {
    setVocabRevealed(false);
    setVocabIndex((prev) => Math.min(vocabLines.length - 1, prev + 1));
  };

  useEffect(() => {
    setIsTaskInteracted(false);
    setInteractionStartTime(null);
  }, [activeStep, check]);

  const handleTaskStart = () => {
    if (!isTaskInteracted) {
      setIsTaskInteracted(true);
      setInteractionStartTime(Date.now());
    }
  };
  
  useEffect(() => {
    const text = lessons && lessons[check] && lessons[check].text ? lessons[check].text : '';
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0);

    const parsed = lines.map((line) => {
      const parts = line.split(/\s*[-–—−―]\s*/, 2);
      const left = (parts[0] || '').trim();
      const right = (parts[1] || '').trim();
      return { left, right };
    });

    setVocabLines(parsed);
    
    if (lessons[check] && parsed.length > 0) {
      const lessonId = lessons[check].id;
      const savedVocabIndex = localStorage.getItem(`lala_vocab_${studentId}_${lessonId}`);
      const parsedIndex = savedVocabIndex ? parseInt(savedVocabIndex, 10) : 0;
      setVocabIndex(Math.min(parsedIndex, parsed.length - 1));
    } else {
      setVocabIndex(0);
    }

    setVocabRevealed(false);
  }, [lessons, check, studentId]);

  useEffect(() => {
    if (lessons.length > 0 && lessons[check] && vocabLines.length > 0) {
      const lessonId = lessons[check].id;
      localStorage.setItem(`lala_vocab_${studentId}_${lessonId}`, vocabIndex);
    }
  }, [vocabIndex, lessons, check, studentId, vocabLines]);

  useEffect(() => {
    setIsLoadingPage(true);
    api.get(`lesson-api?sort=name&filter[edmap]=${tab}&filter[course_id]=${profile.data.course_id}`)
      .then((result) => {
        const fetchedLessons = result.filter(l => Number(l?.level || 0) < 2);
        setLessons(fetchedLessons);
        
        const savedLessonId = localStorage.getItem(`lala_lesson_${studentId}`);
        const foundIndex = fetchedLessons.findIndex(l => String(l.id) === savedLessonId);
        
        setCheck(foundIndex >= 0 ? foundIndex : 0);
      })
      .catch(() => {
        setLessons([]);
      })
      .finally(() => setIsLoadingPage(false));
  }, [tab, studentId]);

  const handleNextStep = () => {
    if (activeStep < maxStep) {
      setActiveStep((prev) => prev + 1);
      return;
    }

    const currentLesson = lessons[check];
    let hasTask = false;
    
    if (activeStep === 0 && currentLesson.student_material) hasTask = true;
    else if (activeStep === 1 && currentLesson.student_material2) hasTask = true;
    else if (activeStep === 2 && currentLesson.student_material3) hasTask = true;
    else if (activeStep === 3 && currentLesson.student_material4) hasTask = true;
    else if (activeStep === 4 && currentLesson.student_material5) hasTask = true;

    if (hasTask) {
      if (!isTaskInteracted) {
        setSnackbar({ open: true, message: 'Натисніть кнопку Play на завданні, щоб розпочати!' });
        return;
      }

      if (interactionStartTime) {
        const timeSpent = Date.now() - interactionStartTime;
        if (timeSpent < MIN_TASK_TIME_MS) {
          setSnackbar({ open: true, message: 'Попрактикуйся ще, щоб закріпити матеріал' });
          return;
        }
      }
    }

    const nextStep = activeStep + 1;
    setActiveStep(nextStep);
    setMaxStep(nextStep);
  };

  const getCurrentMaterial = () => {
    if (!lessons[check]) return null;
    const lesson = lessons[check];
    if (activeStep === 0) return lesson.student_material;
    if (activeStep === 1) return lesson.student_material2;
    if (activeStep === 2) return lesson.student_material3;
    if (activeStep === 3) return lesson.student_material4;
    if (activeStep === 4) return lesson.student_material5;
    return null;
  };

  if (isLoadingPage) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', width: '100%' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  const currentMaterial = getCurrentMaterial();

  return (
    <>
      <Helmet>
        <title> Уроки | Lala English </title>
      </Helmet>

      <Alert severity="error" sx={{ mb: 5, pl: 3 }}>
        Виконуй уроки послідовно, не забувай робити завдання під картинками справа.
      </Alert>
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <FormControl sx={{ width: '300px' }}>
            <InputLabel id="demo-simple-select-label">Урок</InputLabel>
            <Select
              value={lessons.length > 0 ? check : ''}
              label="Урок"
              onChange={handleLessonChange}
            >
              {lessons.map((value, index) => (
                <MenuItem key={value.id} value={index}>{value.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <ToggleButtonGroup value={tab} exclusive onChange={handleTabChange}>
             <ToggleButton value={1}><Typography>Ed Map 1</Typography></ToggleButton>
             <ToggleButton value={2}><Typography>Ed Map 2</Typography></ToggleButton>
             <ToggleButton value={3}><Typography>Ed Map 3</Typography></ToggleButton>
             <ToggleButton value={4}><Typography>Ed Map 4</Typography></ToggleButton>
             <ToggleButton value={5}><Typography>Ed Map 5</Typography></ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {lessons.length ? (
              <Card sx={{ height: 'auto', display: 'flex', flexDirection: 'column', mb: 3 }}>
                <CardHeader title={`Завдання № ${activeStep + 1}`} />
                
                <Box sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  {currentMaterial ? (
                    <TaskIframe 
                      key={`${check}-${activeStep}`} 
                      htmlContent={currentMaterial} 
                      onInteract={handleTaskStart} 
                    />
                  ) : (
                    <Typography component="span" sx={{ color: 'text.secondary', fontSize: 14, textAlign: 'center', width: '100%', py: 5 }}>
                      В цьому уроці немає завдань
                    </Typography>
                  )}
                </Box>

                <div className="hideword" />
                <Stepper nonLinear activeStep={activeStep} sx={{ padding: '10px' }}>
                  {[...Array(5)].map((_, index) => (
                    <Step key={index} completed={index < maxStep}>
                      <StepButton 
                        onClick={() => {
                          if (index <= maxStep) {
                            setActiveStep(index);
                          }
                        }}
                      />
                    </Step>
                  ))}
                </Stepper>
                
                <Box sx={{ display: 'flex', flexDirection: 'row', padding: '10px' }}>
                  <Button
                    color="inherit"
                    disabled={activeStep === 0}
                    onClick={() => setActiveStep(activeStep - 1)}
                    sx={{ mr: 1 }}
                  >
                    Назад
                  </Button>
                  <Box sx={{ flex: '1 1 auto' }} />
                  
                  <Button 
                    disabled={activeStep === 4} 
                    onClick={handleNextStep} 
                    sx={{ mr: 1 }}
                  >
                    Наступне завдання
                  </Button>
                </Box>
              </Card>
            ) : null}

            {lessons.length && lessons[check] && lessons[check].text ? (
              <Card sx={{ paddingBottom: '20px', mt: 3 }}>
                <CardHeader title="Текст для словника" />
                <CardContent>
                  {vocabLines && vocabLines.length ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <Box
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          if (!vocabRevealed) {
                            setVocabRevealed(true);
                          } else {
                            setVocabIndex((i) => (i + 1) % vocabLines.length);
                            setVocabRevealed(false);
                          }
                        }}
                        sx={{
                          position: 'relative',
                          width: '100%', p: 3, borderRadius: 1, boxShadow: 3,
                          textAlign: 'center', cursor: 'pointer', userSelect: 'none',
                          bgcolor: vocabRevealed ? '#FFB74D' : '#FFEB3B',
                          color: vocabRevealed ? '#fff' : '#000',
                          transition: 'all 260ms ease',
                          transform: vocabRevealed ? 'scale(1.02)' : 'scale(1)'
                        }}
                      >
                        <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}>
                          {!vocabRevealed && (
                            <IconButton 
                              onClick={(e) => handleSpeak(vocabLines[vocabIndex].left, e)} 
                              size="small"
                              sx={{ color: 'inherit' }}
                            >
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                              </svg>
                            </IconButton>
                          )}
                        </Box>

                        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
                          {capitalizeWords(vocabRevealed && vocabLines[vocabIndex].right ? vocabLines[vocabIndex].right : vocabLines[vocabIndex].left)}
                        </Typography>
                      </Box>

                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between', 
                          width: '100%', 
                          mt: 2,
                          px: 1
                        }}
                      >
                        <Button
                          onClick={handlePrevWord}
                          disabled={vocabIndex === 0}
                        >
                          Назад
                        </Button>

                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {vocabIndex + 1}/{vocabLines.length}
                        </Typography>

                        <Button
                          onClick={handleNextWord}
                          disabled={vocabIndex === vocabLines.length - 1}
                        >
                          Далі
                        </Button>
                      </Box>

                    </Box>
                  ) : (
                    <Typography component="div" sx={{ fontSize: 14, ml: '24px', mt: '10px' }}>
                      В цьому уроці немає тексту для словника
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ) : null}
          </Grid>

          <Grid item xs={12} md={6}>
            {lessons.length && lessons[check] && lessons[check].image ? (
              <>
                <Card sx={{ paddingBottom: '20px', mb: 3 }}>
                  <CardHeader title={lessons[check].task} />
                  <Box m={'20px'} mt={1} mb={0}>
                    <img src={lessons[check].image} alt="" style={{ maxWidth: '100%', height: 'auto' }} />
                  </Box>
                </Card>
                <Card sx={{ mt: 3 }}>
                  <CardContent>
                    <CreateMessage type={5} student={profile.data} lessonid={lessons[check].id} offlink success={() => setSnackbar({ open: true, message: 'Відповідь відправлено!' })} />
                  </CardContent>
                </Card>
              </>
            ) : null}
          </Grid>
        </Grid>

        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={4000} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="info" variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>

      </Container>
    </>
  );
}